<template>
  <div class="wl-shell">
    <!-- =========================================================
         Nagłówek: tytuł z zielonym akcentem (biała = zaufane)
         + zestaw akcji. W trybie listy delikatny podtytuł kontekstu.
         ========================================================= -->
    <header class="wl-header">
      <!-- Powrót z formularza — przy LEWEJ krawędzi nagłówka, przed tytułem.
           Wszystkie przyciski powrotu w aplikacji stoją po lewej (paski akcji
           kreatorów w bieżącym pomiarze, archiwum i statystykach), więc ten
           trzymał się wcześniej niespójnie prawej strony. -->
      <button
        v-if="currentView !== 'list'"
        type="button"
        class="button-add-user wl-header__back"
        @click="backToList"
      >
        <span class="ds-btn__arrow" aria-hidden="true">&#8592;</span>
        {{ t('back-to-list') }}
      </button>

      <div class="wl-header__title-wrap">
        <span class="wl-header__accent" aria-hidden="true"></span>
        <div>
          <h1 class="wl-header__title">
            {{ pageTitle }}
            <span
              v-if="currentView === 'list' && vehicles.length > 0"
              class="wl-header__count"
              :aria-label="t('wanted-stats-plates')"
            >{{ vehicles.length }}</span>
          </h1>
          <p v-if="currentView === 'list'" class="wl-header__subtitle">
            {{ t('wanted-subtitle-white') }}
          </p>
        </div>
      </div>

      <div class="wl-header__actions" v-if="currentView === 'list'">
        <button
          type="button"
          class="button-save"
          :disabled="!hasContactGroups"
          :title="!hasContactGroups ? t('no-contact-groups') : ''"
          @click="openAdd"
        >
          <span class="wl-btn-icon" aria-hidden="true">+</span>
          {{ t('add') }}
        </button>
        <button
          type="button"
          class="button-add-user"
          :disabled="!hasContactGroups"
          @click="openCsv"
        >
          {{ t('import-csv') }}
        </button>
      </div>
    </header>

    <!-- =========================================================
         Pasek statystyk — tylko na widoku listy.
         ========================================================= -->
    <section
      v-if="currentView === 'list' && vehicles.length > 0"
      class="wl-stats"
      :aria-label="t('statistics')"
    >
      <div class="wl-stat">
        <span class="wl-stat__num">{{ vehicles.length }}</span>
        <span class="wl-stat__label">{{ t('wanted-stats-plates') }}</span>
      </div>
      <div class="wl-stat wl-stat--hits">
        <span class="wl-stat__num">{{ totalHits }}</span>
        <span class="wl-stat__label">{{ t('wanted-stats-hits') }}</span>
      </div>
      <div class="wl-stat wl-stat--groups">
        <span class="wl-stat__num">{{ activeGroupsCount }}</span>
        <span class="wl-stat__label">{{ t('wanted-stats-groups') }}</span>
      </div>
    </section>

    <p v-if="!hasContactGroups && currentView === 'list'" class="wl-alert wl-alert--warning" role="note">
      <span class="wl-alert__icon" aria-hidden="true">!</span>
      {{ t('no-contact-groups') }}
    </p>

    <!-- =========================================================
         Pasek filtra — szybkie zawężenie listy bez round-tripa do API.
         ========================================================= -->
    <div
      v-if="currentView === 'list' && vehicles.length > 5"
      class="wl-toolbar"
    >
      <label class="wl-search">
        <svg class="wl-search__icon" viewBox="0 0 20 20" aria-hidden="true">
          <circle cx="9" cy="9" r="6.5" fill="none" stroke="currentColor" stroke-width="1.7" />
          <line x1="14" y1="14" x2="18" y2="18" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
        </svg>
        <input
          v-model="filterQuery"
          type="search"
          class="wl-search__input"
          :placeholder="t('wanted-filter-placeholder')"
          :aria-label="t('search')"
          autocomplete="off"
        />
        <button
          v-if="filterQuery"
          type="button"
          class="wl-search__clear"
          :aria-label="t('cancel')"
          @click="filterQuery = ''"
        >×</button>
      </label>
      <span class="wl-toolbar__count" aria-live="polite">
        {{ filteredVehicles.length }} / {{ vehicles.length }}
      </span>
    </div>

    <transition name="wanted-fade" mode="out-in">
      <!-- ========================== LISTA ========================== -->
      <div v-if="currentView === 'list'" key="list">
        <div v-if="vehicles.length > 0" class="wl-table-card">
          <table class="wl-table">
            <thead>
              <tr>
                <th class="wl-col-num">{{ t('ordinal-number') }}</th>
                <th class="wl-col-plate">{{ t('short-plate-no') }}</th>
                <th class="wl-col-hits">{{ t('hits-no') }}</th>
                <th class="wl-col-since">{{ t('monitored-since') }}</th>
                <th class="wl-col-desc">{{ t('vehicle-description') }}</th>
                <th class="wl-col-notifies">{{ t('wanted-notifies-column') }}</th>
                <th class="wl-col-action">{{ t('action') }}</th>
              </tr>
            </thead>
            <transition-group tag="tbody" name="wl-row">
              <tr
                v-for="(vehicle, index) in filteredVehicles"
                :key="vehicle.id"
                :class="{ 'wl-row--pending': pendingDeleteId === vehicle.id }"
              >
                <td class="wl-col-num">{{ index + 1 }}</td>
                <td class="wl-col-plate">
                  <span class="wl-plate">{{ vehicle.licensePlate }}</span>
                </td>
                <td class="wl-col-hits">
                  <span
                    class="wl-hits"
                    :class="{ 'wl-hits--zero': vehicle.hits === 0, 'wl-hits--many': vehicle.hits >= 10 }"
                  >{{ vehicle.hits }}</span>
                </td>
                <td class="wl-col-since">{{ formatDateTime(vehicle.monitoredSince) }}</td>
                <td class="wl-col-desc">
                  <span v-if="vehicle.description">{{ vehicle.description }}</span>
                  <span v-else class="wl-readonly">—</span>
                </td>
                <td class="wl-col-notifies">
                  <div v-if="vehicle.contactGroups.length > 0" class="wl-chips">
                    <ContactGroupChip
                      v-for="g in vehicle.contactGroups"
                      :key="g.id"
                      :group="g"
                      :clickable="false"
                    />
                  </div>
                  <span v-else class="wl-readonly">—</span>
                </td>
                <td class="wl-col-action">
                  <template v-if="canEditPlate(vehicle)">
                    <!-- Dwustopniowe usuwanie: szybciej dla power-userów,
                         bezpieczniej dla pomyłek („potwierdź" zamiast modala). -->
                    <div v-if="pendingDeleteId === vehicle.id" class="wl-confirm">
                      <button
                        type="button"
                        class="button-delete button-compact"
                        @click="confirmDelete(vehicle.id)"
                      >{{ t('wanted-confirm-delete') }}</button>
                      <button
                        type="button"
                        class="button-add-user button-compact"
                        @click="cancelDelete"
                      >{{ t('cancel') }}</button>
                    </div>
                    <button
                      v-else
                      type="button"
                      class="button-delete button-compact"
                      @click="requestDelete(vehicle.id)"
                    >{{ t('delete') }}</button>
                  </template>
                  <span v-else class="wl-readonly">—</span>
                </td>
              </tr>
            </transition-group>
          </table>

          <!-- Brak wyników po filtrze — odróżniony od pustej listy. -->
          <div v-if="filteredVehicles.length === 0" class="wl-empty-filter">
            <p>{{ t('wanted-filter-no-results') }}</p>
            <button
              type="button"
              class="button-add-user button-compact"
              @click="filterQuery = ''"
            >{{ t('cancel') }}</button>
          </div>
        </div>

        <!-- Empty state — wyraziste, ale spokojne. -->
        <div v-else class="wl-empty">
          <svg class="wl-empty__icon" viewBox="0 0 64 64" aria-hidden="true">
            <rect x="8" y="20" width="48" height="24" rx="3" fill="#fff" stroke="#cfcfca" stroke-width="2" />
            <rect x="14" y="26" width="36" height="12" rx="2" fill="#f8f8f5" stroke="#e3e3de" stroke-width="1.5" />
            <path d="M22 30h20M22 34h12" stroke="#b5b5b1" stroke-width="1.6" stroke-linecap="round" />
            <circle cx="48" cy="48" r="9" fill="#fff" stroke="#1f8f2f" stroke-width="2" />
            <path d="M44 48l3 3 5-6" fill="none" stroke="#1f8f2f" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <p class="wl-empty__title">{{ t('no-vehicles-on-white-list') }}</p>
        </div>
      </div>

      <!-- ====================== INNE WIDOKI ======================= -->
      <WantedPlateForm
        v-else-if="currentView === 'add'"
        key="add"
        list-type="white"
        :validity-period-enabled="false"
        :submitting="actionSubmitting"
        @submit="onAddSubmit"
        @cancel="backToList"
      />

      <WantedCsvImport
        v-else
        key="csv"
        list-type="white"
        :available-groups="wantedStore.contactGroups"
        :submitting="actionSubmitting"
        @submit="onCsvSubmit"
        @cancel="backToList"
      />
    </transition>

  </div>
</template>

<script>
import WhiteListsService from '@/services/WhiteListsService';
import ContactGroupChip from '@/components/wanted/ContactGroupChip.vue';
import WantedPlateForm from '@/components/wanted/WantedPlateForm.vue';
import WantedCsvImport from '@/components/wanted/WantedCsvImport.vue';
import { useUserStore } from '@/stores/user.store';
import { useWantedStore } from '@/stores/wanted.store';
import { useNotificationStore } from '@/stores/notification.store';

export default {
  name: 'WhiteList',
  components: {
    ContactGroupChip,
    WantedPlateForm,
    WantedCsvImport,
  },
  data() {
    return {
      vehicles: [],
      currentView: 'list', // 'list' | 'add' | 'csv'
      actionSubmitting: false,
      // Szybki filtr w-locie + dwustopniowe usuwanie.
      filterQuery: '',
      pendingDeleteId: null,
    };
  },
  computed: {
    wantedStore() { return useWantedStore(); },
    hasContactGroups() { return this.wantedStore.contactGroups.length > 0; },
    pageTitle() {
      if (this.currentView === 'add') return this.t('add-to-white-list');
      if (this.currentView === 'csv') return `${this.t('import-csv')} → ${this.t('white-list')}`;
      return this.t('white-list').toUpperCase();
    },
    filteredVehicles() {
      const q = this.filterQuery.trim().toLowerCase();
      if (!q) return this.vehicles;
      return this.vehicles.filter(v => {
        const plate = (v.licensePlate || '').toLowerCase();
        const desc  = (v.description  || '').toLowerCase();
        return plate.includes(q) || desc.includes(q);
      });
    },
    totalHits() {
      return this.vehicles.reduce((sum, v) => sum + (Number(v.hits) || 0), 0);
    },
    activeGroupsCount() {
      const ids = new Set();
      this.vehicles.forEach(v => v.contactGroupIds.forEach(id => ids.add(id)));
      return ids.size;
    },
  },
  methods: {
    mapVehicleFromApi(vehicle) {
      const brief = Array.isArray(vehicle.contactGroupsBrief) ? vehicle.contactGroupsBrief : [];
      return {
        id: vehicle.id,
        licensePlate: vehicle.plateLpr,
        description: vehicle.description ?? '',
        hits: vehicle.hitCounter ?? 0,
        monitoredSince: vehicle.createdAt || new Date().toISOString(),
        contactGroupIds: Array.isArray(vehicle.contactGroupIds) ? vehicle.contactGroupIds : brief.map(g => g.id),
        contactGroups: brief.map(g => ({
          id: g.id,
          name: g.name,
          contactsSummary: g.contactsSummary ?? { count: 0, emails: 0, sms: 0, mms: 0 },
        })),
      };
    },
    canEditPlate(vehicle) {
      const userStore = useUserStore();
      return userStore.canEditWantedPlate({ contactGroupIds: vehicle.contactGroupIds });
    },
    async fetchVehicles() {
      try {
        const response = await WhiteListsService.getAll();
        this.vehicles = (Array.isArray(response) ? response : []).map(this.mapVehicleFromApi);
      } catch (error) {
        if (error === 404 || error?.status === 404) {
          this.vehicles = [];
          return;
        }
        console.error('Error fetching vehicles:', error);
        this.vehicles = [];
      }
    },
    requestDelete(id) {
      this.pendingDeleteId = id;
    },
    cancelDelete() {
      this.pendingDeleteId = null;
    },
    async confirmDelete(id) {
      this.pendingDeleteId = null;
      try {
        await WhiteListsService.delete(id);
        this.vehicles = this.vehicles.filter(v => v.id !== id);
        this.showStatus(this.t('wanted-plate-deleted-white'), 'success');
      } catch (error) {
        console.error('Error deleting vehicle:', error);
        this.showStatus(this.t('wanted-plate-delete-error'), 'error');
      }
    },
    formatDateTime(dateString) {
      const date = new Date(dateString);
      const pad = n => String(n).padStart(2, '0');
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    },
    openAdd() {
      if (!this.hasContactGroups) {
        this.showStatus(this.t('no-contact-groups'), 'error');
        return;
      }
      this.currentView = 'add';
    },
    openCsv() {
      if (!this.hasContactGroups) {
        this.showStatus(this.t('no-contact-groups'), 'error');
        return;
      }
      this.currentView = 'csv';
    },
    backToList() {
      this.currentView = 'list';
      this.pendingDeleteId = null;
    },
    async onAddSubmit(payload) {
      this.actionSubmitting = true;
      try {
        const response = await WhiteListsService.postNewPlate(
          payload.plateLpr,
          payload.description,
          payload.contactGroupIds
        );
        this.vehicles.unshift(this.mapVehicleFromApi(response));
        this.showStatus(this.t('wanted-plate-added-white'), 'success');
        this.currentView = 'list';
      } catch (error) {
        console.error('Error adding vehicle:', error);
        this.showStatus(this.parseApiError(error, this.t('wanted-plate-add-error')), 'error');
      } finally {
        this.actionSubmitting = false;
      }
    },
    async onCsvSubmit({ rows, contactGroupIds }) {
      this.actionSubmitting = true;
      let success = 0;
      let failed = 0;
      for (const row of rows) {
        try {
          const response = await WhiteListsService.postNewPlate(
            row.licensePlate,
            row.description,
            contactGroupIds
          );
          this.vehicles.unshift(this.mapVehicleFromApi(response));
          success++;
        } catch (error) {
          console.error('Error adding vehicle from CSV:', error);
          failed++;
        }
      }
      this.actionSubmitting = false;
      this.currentView = 'list';
      this.showStatus(
        this.t('wanted-csv-import-result', { success, failed }),
        failed === 0 ? 'success' : 'error'
      );
    },
    parseApiError(error, fallback) {
      if (typeof error === 'string' && error.trim() !== '') return error;
      if (error?.message) return error.message;
      return fallback;
    },
    // Wynik operacji pokazuje globalny komponent powiadomień (AppNotifications).
    showStatus(message, type = 'error') {
      useNotificationStore().push(message, type);
    },
  },
  async mounted() {
    await this.wantedStore.fetchContactGroups();
    await this.fetchVehicles();
  }
};
</script>

<style scoped>
/* =============================================================
   Lokalna paleta — zieleń jako tożsamość białej listy (zaufane),
   neutralne szarości jako tło i ramy. Trzymamy się tej samej
   strukury i miar co czarna lista — różni je tylko akcent.
   ============================================================= */
.wl-shell {
  --wl-green: var(--status-ok);
  --wl-green-deep: var(--status-ok);
  --wl-ink: var(--text-primary);
  --wl-ink-soft: var(--text-secondary);
  --wl-muted: var(--text-muted);
  --wl-line: var(--border-default);
  --wl-line-soft: var(--border-subtle);
  --wl-bg: var(--surface-secondary);
  --wl-bg-hover: var(--surface-hover);

  padding: 1.5rem 1.3rem 2.5rem;
  /* Stabilna szerokość: wypełniamy parent (.content-wrap) i nigdy go nie rozpychamy.
     Bez `min-width: 0` flex-child puchnie do natywnej szerokości tabeli i wpycha
     się w obszar lewego paska filtrów. */
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  margin: 0;
  color: var(--wl-ink);
}

/* =============================================================
   Header sekcji — pionowy akcent, tytuł, podtytuł i akcje.
   ============================================================= */
.wl-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--wl-line);
  position: relative;
}
.wl-header__title-wrap {
  display: flex;
  align-items: stretch;
  gap: 0.9rem;
  flex: 1;
  min-width: 0;
}
.wl-header__accent {
  width: 5px;
  border-radius: 3px;
  background: var(--wl-green);
  box-shadow: none;
  flex-shrink: 0;
}
.wl-header__title {
  margin: 0;
  font-size: var(--font-size-section);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.04em;
  line-height: 1.15;
  color: var(--wl-ink);
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
}
.wl-header__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 1.6rem;
  padding: 0 0.55rem;
  border-radius: 999px;
  background: var(--wl-green);
  color: var(--text-inverse);
  font-size: 0.85rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0;
  font-variant-numeric: tabular-nums;
  box-shadow: var(--shadow-panel);
}
.wl-header__subtitle {
  margin: 0.35rem 0 0;
  font-size: 0.88rem;
  color: var(--wl-muted);
  letter-spacing: 0.01em;
}
.wl-header__back {
  flex-shrink: 0;
  align-self: center;
}
.wl-header__actions {
  display: flex;
  gap: 0.55rem;
  flex-shrink: 0;
  align-self: center;
}
.wl-btn-icon {
  font-weight: var(--font-weight-bold);
  font-size: 1rem;
  line-height: 1;
}

/* =============================================================
   Pasek statystyk — 3 tile'y; tabular-nums dla równego prawego kantu.
   ============================================================= */
.wl-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.85rem;
  margin-bottom: 1rem;
}
.wl-stat {
  position: relative;
  background-color: var(--surface-primary);
  border: 1px solid var(--wl-line);
  border-radius: var(--panel-radius);
  padding: 0.85rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  overflow: hidden;
  box-shadow: var(--shadow-panel);
  transition: box-shadow var(--transition-fast), border-color var(--transition-fast);
}
.wl-stat:hover {
  box-shadow: var(--shadow-panel);
}
.wl-stat::before {
  /* Kropkowane tło — bardzo subtelne, dla wartości artystycznej. */
  content: "";
  position: absolute;
  right: -10px;
  top: -10px;
  width: 80px;
  height: 80px;
  background: var(--surface-secondary);
  background-size: 9px 9px;
  pointer-events: none;
}
.wl-stat--hits::before    { background: var(--surface-secondary); background-size: 9px 9px; }
.wl-stat--groups::before  { background: var(--surface-secondary); background-size: 9px 9px; }
.wl-stat__num {
  font-size: 1.7rem;
  font-weight: var(--font-weight-bold);
  line-height: 1.1;
  color: var(--wl-ink);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}
.wl-stat__label {
  font-size: 0.75rem;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--wl-muted);
}

/* =============================================================
   Toolbar z filtrem — pojawia się przy > 5 wpisów.
   ============================================================= */
.wl-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.wl-search {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  background-color: var(--surface-primary);
  border: 1px solid var(--wl-line);
  border-radius: var(--control-radius);
  padding: 0 0.6rem 0 2.25rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  height: var(--control-height);
}
.wl-search:focus-within {
  border-color: var(--wl-green);
  box-shadow: 0 0 0 2px var(--accent-focus-ring);
}
.wl-search__icon {
  position: absolute;
  left: 0.7rem;
  top: 50%;
  width: 16px;
  height: 16px;
  transform: translateY(-50%);
  color: var(--wl-muted);
  pointer-events: none;
}
.wl-search__input {
  flex: 1;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  color: var(--wl-ink);
  height: 100%;
  padding: 0;
}
.wl-search__input::placeholder { color: var(--border-strong); }
.wl-search__input::-webkit-search-decoration,
.wl-search__input::-webkit-search-cancel-button { display: none; }
.wl-search__clear {
  background: transparent;
  border: 0;
  font-size: 1.2rem;
  cursor: pointer;
  color: var(--wl-muted);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  min-width: 22px !important;
  min-height: 22px !important;
  line-height: 1;
}
.wl-search__clear:hover { color: var(--wl-green); background-color: var(--status-ok-soft) !important; }
.wl-toolbar__count {
  font-size: 0.82rem;
  font-weight: var(--font-weight-medium);
  color: var(--wl-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* =============================================================
   Karta z tabelą — soft shadow, sticky thead, polish na hover.
   ============================================================= */
.wl-table-card {
  background-color: var(--surface-primary);
  border: 1px solid var(--wl-line);
  border-radius: var(--panel-radius);
  /* Wewnętrzny scroll w poziomie — gdy okno za wąskie, tabela przewija się
     wewnątrz karty zamiast rozpychać cały widok. */
  overflow-x: auto;
  overflow-y: hidden;
  box-shadow: var(--shadow-panel);
  margin-bottom: 1rem;
}
.wl-table {
  width: 100%;
  /* `fixed` — szerokości kolumn z CSS są respektowane, treść nie rozpycha tabeli. */
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
}
/* Długie opisy / nazwy grup łamią się zamiast wymuszać poziomy scroll. */
.wl-table td { word-break: break-word; overflow-wrap: anywhere; }
.wl-table th,
.wl-table td {
  padding: 0.75rem 0.95rem;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid var(--wl-line-soft);
}
.wl-table th.wl-col-num,
.wl-table td.wl-col-num,
.wl-table th.wl-col-hits,
.wl-table td.wl-col-hits { text-align: right; }
.wl-table th.wl-col-action,
.wl-table td.wl-col-action { text-align: right; white-space: nowrap; }
.wl-table thead th {
  background-color: var(--surface-secondary);
  font-weight: var(--font-weight-bold);
  color: var(--wl-ink-soft);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 2px solid var(--wl-ink);
  position: sticky;
  top: 0;
  z-index: 1;
  white-space: nowrap;
}
.wl-table tbody tr {
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}
.wl-table tbody tr:nth-child(even) {
  background-color: var(--wl-bg);
}
.wl-table tbody tr:hover {
  background-color: var(--wl-bg-hover);
  box-shadow: inset 3px 0 0 var(--wl-green);
}
.wl-table tbody tr:last-child td { border-bottom: 0; }
.wl-table tbody tr.wl-row--pending {
  background-color: var(--status-error-soft);
  box-shadow: inset 3px 0 0 var(--status-error-strong);
}

/* Procentowe szerokości — tabela skaluje się razem z kontenerem. */
.wl-col-num      { width: 4%;  color: var(--wl-muted); font-variant-numeric: tabular-nums; }
.wl-col-plate    { width: 13%; }
.wl-col-hits     { width: 7%;  font-variant-numeric: tabular-nums; }
.wl-col-since    { width: 15%; white-space: nowrap; color: var(--wl-ink-soft); font-variant-numeric: tabular-nums; }
.wl-col-desc     { width: 16%; color: var(--wl-ink-soft); }
.wl-col-notifies { width: 33%; }
.wl-col-action   { width: 12%; }

td.wl-col-notifies { overflow: hidden; }

/* Tablica rejestracyjna — wizualnie "blacha", monospaced. */
.wl-plate {
  display: inline-block;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace;
  font-weight: var(--font-weight-bold);
  font-size: 0.95rem;
  letter-spacing: 0.08em;
  padding: 0.5rem;
  border: 1.5px solid var(--wl-ink);
  border-radius: var(--control-radius);
  background: var(--surface-secondary);
  color: var(--wl-ink);
  box-shadow:
    inset 0 -2px 0 rgba(31, 41, 55, 0.08),
    0 1px 0 rgba(255, 255, 255, 0.7);
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
}

/* Licznik trafień. */
.wl-hits {
  display: inline-flex;
  justify-content: center;
  min-width: 2rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background-color: var(--surface-secondary);
  border: 1px solid var(--wl-line);
  font-weight: var(--font-weight-bold);
  font-size: 0.85rem;
  color: var(--wl-ink-soft);
}
.wl-hits--zero  { background-color: transparent; border-color: transparent; color: var(--border-strong); font-weight: 500; }
.wl-hits--many  { background-color: var(--status-ok-soft); border-color: var(--status-ok-border); color: var(--wl-green-deep); }

.wl-chips {
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
  min-width: 0;
  max-width: 100%;
}
.wl-readonly { color: var(--border-strong); font-weight: 500; }

/* Dwustopniowe usuwanie. */
.wl-confirm {
  display: inline-flex;
  gap: 0.3rem;
  justify-content: flex-end;
  flex-wrap: wrap;
  max-width: 100%;
}
.wl-confirm > .button-compact { flex: 0 0 auto; }

/* =============================================================
   Empty states.
   ============================================================= */
.wl-empty {
  background-color: var(--surface-primary);
  border: 1px dashed var(--border-default);
  border-radius: var(--panel-radius);
  padding: 3rem 1.5rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}
.wl-empty__icon { width: 80px; height: 80px; opacity: 0.85; }
.wl-empty__title { margin: 0; color: var(--wl-ink-soft); font-size: 0.95rem; }

.wl-empty-filter {
  padding: 1.5rem;
  text-align: center;
  color: var(--wl-muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}
.wl-empty-filter p { margin: 0; }

/* =============================================================
   Alerty — kompaktowe pigułki z lewym paskiem koloru.
   ============================================================= */
.wl-alert {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  border-radius: var(--control-radius);
  margin-bottom: 1rem;
  font-size: 0.92rem;
  line-height: 1.45;
  border: 1px solid transparent;
}
.wl-alert__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-weight: var(--font-weight-bold);
  font-size: 0.85rem;
  flex-shrink: 0;
  color: var(--text-inverse);
}
.wl-alert__msg { flex: 1; }
.wl-alert--warning {
  background-color: var(--status-warn-soft);
  border-color: var(--status-warn-border);
  border-left: 4px solid var(--status-warn);
  color: var(--status-warn-strong);
}
.wl-alert--warning .wl-alert__icon { background: var(--status-warn); }


/* =============================================================
   Animacje przejść między widokami i wierszami.
   ============================================================= */
.wanted-fade-enter-active,
.wanted-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.wanted-fade-enter-from { opacity: 0; transform: translateY(6px); }
.wanted-fade-leave-to   { opacity: 0; transform: translateY(-6px); }

.wl-row-enter-active,
.wl-row-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease, background-color 0.25s ease;
}
.wl-row-enter-from {
  opacity: 0;
  transform: translateX(-12px);
  background-color: var(--status-ok-soft);
}
.wl-row-leave-to {
  opacity: 0;
  transform: translateX(12px);
  background-color: var(--status-error-soft);
}
.wl-row-leave-active { position: relative; }


/* =============================================================
   Override globalnego button-system.css tylko gdzie konieczne.
   ============================================================= */
.button-save {
  border-radius: 6px !important;
  padding: 0.45rem 0.9rem !important;
}

.button-delete {
  border-radius: 4px;
  padding: 0.45rem 0.75rem;
}

@media (max-width: 900px) {
  .wl-stats { grid-template-columns: 1fr 1fr; }
  .wl-stat--groups { grid-column: span 2; }
}
@media (max-width: 768px) {
  .wl-shell { padding: 1rem; }
  .wl-header { flex-direction: column; align-items: stretch; gap: 0.75rem; }
  .wl-header__actions { width: 100%; }
  /* Kolumna na wąskich ekranach — powrót nad tytułem, ale bez rozciągania
     na całą szerokość (kontener ma `align-items: stretch`). */
  .wl-header__back { align-self: flex-start; }
  .wl-stats { grid-template-columns: 1fr; }
  .wl-stat--groups { grid-column: auto; }
  .wl-table { font-size: 0.85rem; }
  .wl-table th, .wl-table td { padding: 0.6rem 0.55rem; }
}
</style>
